import { create } from 'apisauce';
import { refreshToken } from './tokenService';
import removeLocalStorage from '@/utils/removeLocalStorage';

export const BASE_URL = process.env.REACT_APP_API_URL;
export const FTP_URL = process.env.REACT_APP_FTP_URL;

let isRefreshing = false;
let refreshPromise = null;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });

  failedQueue = [];
};

const resetRefreshState = () => {
  isRefreshing = false;
  refreshPromise = null;
  failedQueue = [];
};

export const API = create({
  baseURL: BASE_URL,
  headers: {
    accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

API.axiosInstance.interceptors.request.use(async (config) => {
  const urlPath = config?.url || '';
  const isAuthEndpoint = /\/(login|register|refresh)\b/.test(urlPath);

  let token = localStorage.getItem('token');
  const expiry = parseInt(localStorage.getItem('tokenExpiry'), 10);

  // For auth endpoints, never attach auth headers or attempt refresh
  if (isAuthEndpoint) {
    if (config.headers && config.headers.Authorization)
      delete config.headers.Authorization;
    return config;
  }

  // If there's no token, don't attach header and never try to refresh
  if (!token) {
    if (config.headers && config.headers.Authorization)
      delete config.headers.Authorization;
    return config;
  }

  // Always attach current token (even if expired) for non-auth endpoints
  config.headers.Authorization = `Bearer ${token}`;

  // Handle refresh if expired (only when a token exists)
  if (expiry && Date.now() > expiry) {
    // Check if we recently attempted refresh and it might have been cancelled
    const lastRefreshAttempt = parseInt(
      localStorage.getItem('lastRefreshAttempt'),
      10
    );
    const recentlyAttempted =
      lastRefreshAttempt && Date.now() - lastRefreshAttempt < 5000; // 5 seconds

    if (isRefreshing) {
      // If already refreshing, queue this request
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((newToken) => {
          config.headers.Authorization = `Bearer ${newToken}`;
          return config;
        })
        .catch((err) => {
          return Promise.reject(err);
        });
    }

    // If we recently attempted refresh but it might have been cancelled, reset state and retry
    if (recentlyAttempted && !isRefreshing) {
      console.log('Previous refresh might have been cancelled, retrying...');
      resetRefreshState();
    }

    isRefreshing = true;
    localStorage.setItem('lastRefreshAttempt', Date.now().toString());

    refreshPromise = refreshToken()
      .then((newToken) => {
        localStorage.setItem('token', newToken);
        localStorage.setItem('tokenExpiry', Date.now() + 23 * 60 * 60 * 1000);
        localStorage.setItem('lastRefreshTime', Date.now());
        processQueue(null, newToken);
        return newToken;
      })
      .catch((err) => {
        console.error('Token refresh failed:', err);
        processQueue(err, null);
        // Don't clear localStorage immediately, let the response interceptor handle it
        throw err;
      })
      .finally(() => {
        isRefreshing = false;
        refreshPromise = null;
      });

    try {
      const newToken = await refreshPromise;
      config.headers.Authorization = `Bearer ${newToken}`;
    } catch (err) {
      // If refresh failed, reset state for next attempt
      resetRefreshState();
      return Promise.reject(err);
    }
  }

  return config;
});

API.addResponseTransform((res) => {
  console.log('res', res);
  if (!res.ok) console.error('API error =>', res);

  if (
    res.status === 401 ||
    res.originalError?.message.includes('401') ||
    res.data?.message === 'Not authorized.'
  ) {
    const lastRefresh = parseInt(localStorage.getItem('lastRefreshTime'), 10);
    const justRefreshed = lastRefresh && Date.now() - lastRefresh < 10000; // Increased to 10 seconds

    if (justRefreshed) {
      console.warn('Ignoring 401 from old token right after refresh');
      return;
    }

    // Only clear and redirect if not currently refreshing
    if (!isRefreshing) {
      console.error('401 error - clearing auth and redirecting to login');
      resetRefreshState();
      removeLocalStorage();
      if (window.location.hash !== '') window.location.hash = '';
    }
  }
});

// Handle page unload to clean up refresh state
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    resetRefreshState();
  });
}
